import React, { useRef, useState } from 'react';
import { Avatar, Button, Tabs, Typography, Descriptions, Tag, Dropdown, Menu, Segmented, Form, Select, Space, Modal, Divider, Progress, Tooltip, notification } from 'antd';
import {
    PlusCircleOutlined,
    RobotOutlined,
} from '@ant-design/icons';
import NotesComponent from '../../../../components/Forms/NotesComponent';
import moment from 'moment';
import timeAgo from '../../../../constants/functions';
import EditableFormBlock from '../../../../components/Forms/EditableField';
import { MasterConfigs } from '../../../../interfaces/entity';
import { constants } from '../../../../constants/common';
import ProfileCard from '../../../../components/ContactInfo/ProfileCard';
import Attachments from '../../../Leads/Attachments';
import Timeline from '../../../Leads/Timeline';
import CandidateFeedback from './CandidateFeedback';
import Meetings from './Meetings';
import { useDispatch } from 'react-redux';
import { postData } from '../../../../actions/developer';
import { decryptData, encryptData } from '../../../../actions/crypto';

const { Title, Text, Link } = Typography;
const { TabPane } = Tabs;

const CandidateDetails: React.FC<any> = ({ candidate }) => {
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [highlighted, setHighlighted] = useState<string | null>(null);
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview');
    const dispatch = useDispatch();

    // AI Screening state
    const [loading, setLoading] = useState(false);
    const [showScreeningModal, setShowScreeningModal] = useState(false);
    const [screeningData, setScreeningData] = useState<any>(null);

    // Initialize screening data from candidate
    React.useEffect(() => {
        if (candidate?.aiScreening) {
            setScreeningData(candidate.aiScreening);
        }
    }, [candidate]);

    const editable = ['salary_discussion',
        'pre_ownboarding',
        'doccument_verify',
        'offer_genrate'].includes(candidate.level)

    // Check if candidate has offer CTC and joining date
    const hasOfferCTC = candidate?.offeredCTC && candidate.offeredCTC > 0;
    const hasJoiningDate = candidate?.joiningDate && new Date(candidate.joiningDate).getTime() > 0;
    const isPreOnboardingEligible = hasOfferCTC && hasJoiningDate;

    // Modify level options to disable pre-onboarding if fields are missing
    const getLevelOptions = () => {
        return constants.PROFILE_STATUS_OPTIONS.map(option => {
            if (option.value === 'pre_ownboarding') {
                return {
                    ...option,
                    disabled: !isPreOnboardingEligible
                };
            }
            return option;
        });
    };

    const candidateOwnerConfig: MasterConfigs = {
        api: () => '/candidates',
        onSubmit: async (id, values) => {
            // Validate if trying to change to pre-onboarding
            if (values.level === 'pre_ownboarding' && !isPreOnboardingEligible) {
                Modal.error({
                    title: 'Cannot Change to Pre-Onboarding',
                    content: 'Please add Offer CTC and Joining Date before changing the level to Pre-Onboarding.',
                });
                return Promise.reject(new Error('Missing required fields'));
            }
            return Promise.resolve();
        },
        getFormColumns: () => [
            {
                label: 'HR',
                name: 'candidateOwner',
                type: 'select',
                commonProps: {
                    disabled: true,
                },
                render: (text, data) => {
                    return `${text?.firstName || ''} ${text?.lastName || ''}`
                },
                span: 24,
            },
            {
                label: 'Job Role',
                name: 'profileFor',
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                span: 24,
                render: (text) => text?.name,
            },
            {
                label: 'Candidate Name',
                name: 'firstName',
                span: 24,
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                render: (text, record) => constants.GET_FULL_NAME(record),
            },
            {
                label: 'Email',
                name: 'email',
                type: 'text',
                span: 24,
                commonProps: {
                    disabled: true,
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: 'Phone',
                name: 'phone',
                type: 'text',
                span: 24,
                commonProps: {
                    disabled: true,
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const phoneRegex = /^[0-9]{10,15}$/;
                            return phoneRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid phone number'));
                        },
                    },
                ],
            },
            {
                label: 'Mobile',
                name: 'mobile',
                type: 'text',
                span: 24,
                commonProps: {
                    disabled: true,
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const mobileRegex = /^[0-9]{10,15}$/;
                            return mobileRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid mobile number'));
                        },
                    },
                ],
            },
            {
                label: 'Interview Level',
                name: 'level',
                type: 'select',
                span: 24,
                render: (text) => constants.PROFILE_STATUS_TEXT[text] || text,
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: getLevelOptions(),
            },
        ],
        combo: []
    }

    const candidateConfig: MasterConfigs = {
        api: () => '/candidates',
        getFormColumns: () => [
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Candidate Information</Title>
            },
            {
                label: 'HR',
                name: 'candidateOwner',
                type: 'select',
                isCombo: true,
                render: (text, data) => {
                    return `${text?.firstName || ''} ${text?.lastName || ''}`
                },
                span: 12,
            },
            {
                label: 'Job Role',
                name: 'profileFor',
                key: 'profileFor',
                type: 'select',
                isCombo: true,
                span: 12,
                render: (text) => text?.name,
            },
            {
                label: 'First Name',
                name: 'firstName',
                type: 'text',
                rules: [{ required: true, message: 'First Name is required' }],
                span: 12,
            },
            {
                label: 'Last Name',
                name: 'lastName',
                type: 'text',
                span: 12,
            },
            {
                label: 'Date of Birth',
                name: 'dob',
                type: 'date',
                render: (text) => text ? <Space direction='horizontal' className='gap-2'>
                    {moment(new Date(text)).format('LL')}
                    <Tag color="blue">{moment().diff(moment(new Date(text)), 'years')} Years</Tag>
                </Space> : '-',
            },
            {
                label: 'Gender',
                name: 'gender',
                type: 'select',
            },
            {
                label: 'Email',
                name: 'email',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: 'Phone',
                name: 'phone',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const phoneRegex = /^[0-9]{10,15}$/;
                            return phoneRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid phone number'));
                        },
                    },
                ],
            },
            {
                label: 'Experiance',
                name: 'experiance',
                key: 'experiance',
                span: 12,
                type: 'text',
                inputProps: {
                    addonAfter: 'Years'
                },
                render: (text) => text ? `${text} Years` : 'N/A',
            },
            {
                label: 'CTC',
                name: 'currentCTC',
                key: 'currentCTC',
                span: 12,
                type: 'text',
                inputProps: {
                    addonBefore: 'INR'
                },
                render: (text) => text ? `INR ${text}` : 'N/A',
            },
            {
                label: 'Notice Period',
                name: 'noticePeriod',
                key: 'noticePeriod',
                inputProps: {
                    addonAfter: 'Days'
                },
                render: (text) => text ? `${text} Days` : 'N/A',
                span: 12,
                type: 'text',
            },
            {
                label: 'E-CTC',
                name: 'expectedCTC',
                key: 'expectedCTC',
                span: 12,
                type: 'text',
                inputProps: {
                    addonBefore: 'INR'
                },
                render: (text) => text ? `INR ${text}` : 'N/A',
            },
            {
                label: 'Department',
                name: 'department',
                key: 'department',
                span: 12,
                type: 'select',
                isCombo: true,
                render: (text) => text?.name || text,
            },
            {
                label: 'Last Working Date',
                name: 'lastWorkingDate',
                key: 'lastWorkingDate',
                span: 12,
                type: 'date',
                render: (text) => text ? moment(new Date(text)).format('LL') : 'N/A',
            },
            {
                label: 'Joining Date',
                name: 'joiningDate',
                key: 'joiningDate',
                span: 12,
                type: 'date',
                commonProps: {
                    disabled: !editable,
                },
                render: (text) => text ? moment(new Date(text)).format('LL') : 'N/A',
            },
            {
                label: 'Offered CTC',
                name: 'offeredCTC',
                key: 'offeredCTC',
                span: 12,
                type: 'text',
                commonProps: {
                    disabled: !editable,
                },
                inputProps: {
                    addonBefore: 'INR'
                },
                render: (text) => text ? `INR ${new Date(text)}` : 'N/A',
            },
            {
                label: 'Modified By',
                name: 'updatedBy',
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return <div>
                        {[text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A'}
                        <br />
                        <Text type="secondary" className="text-xs">
                            {moment(new Date(text?.updatedAt)).format('llll')}
                        </Text>
                    </div>
                },
                span: 12,
            },
            {
                label: 'Created By',
                name: 'createdBy',
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return <div>
                        {[text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A'}
                        <br />
                        <Text type="secondary" className="text-xs">
                            {moment(new Date(text?.createdAt)).format('llll')}
                        </Text>
                    </div>
                },
                span: 12,
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Address Information</Title>
            },
            {
                label: 'Street',
                name: ['address', 'street'],
                type: 'text',
                render: (text) => text || 'N/A',
                span: 12,
            },
            {
                label: 'City',
                name: ['address', 'city'],
                render: (text) => text || 'N/A',
                type: 'text',
                span: 12,
            },
            {
                label: 'State',
                name: ['address', 'state'],
                render: (text) => text || 'N/A',
                type: 'text',
                span: 12,
            },
            {
                label: 'Zip Code',
                name: ['address', 'zip'],
                render: (text) => text || 'N/A',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const zipRegex = /^[0-9]{4,10}$/;
                            return zipRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid zip code'));
                        },
                    },
                ],
            },
            {
                label: 'Country',
                type: 'text',
                name: ['address', 'country'],
                render: (text) => text || 'N/A',
                span: 12,
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Description Information</Title>
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 24,
                textAreaProps: {
                    autoSize: { minRows: 3, maxRows: 6 }
                }
            },
        ],
        combo: [
            {
                api: '/users',
                name: 'candidateOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            {
                name: 'profileFor',
                api: '/job-titles',
                idKey: '_id',
                render: (text) => text.name
            },
            {
                name: 'department',
                api: '/departments',
                idKey: '_id',
                render: (text) => text.name
            },
        ],
    }

    const sections = [
        { id: 'ai-screening', title: 'AI Screening' },
        { id: 'notes', title: 'Notes' },
        { id: 'attachments', title: 'Attachments' },
        { id: 'invited-meetings', title: 'Invited Meetings' },
        { id: 'open-activities', title: 'Open Activities' },
        { id: 'closed-activities', title: 'Closed Activities' },
        { id: 'feedback', title: 'Feedback' },
        { id: 'emails', title: 'Emails' },
        { id: 'social', title: 'Social' },
    ];

    // AI Screening functions
    const getScoreColor = (score: number) => {
        if (score >= 80) return '#52c41a';
        if (score >= 60) return '#faad14';
        return '#ff4d4f';
    };

    const getRecommendationColor = (recommendation: string) => {
        if (recommendation?.toLowerCase().includes('strong match')) return '#52c41a';
        if (recommendation?.toLowerCase().includes('proceed with caution')) return '#faad14';
        return '#ff4d4f';
    };

    const handleScreenWithAI = async () => {
        if (!candidate?._id) return;

        setLoading(true);
        try {
            const encryptedId = encryptData(candidate._id);
            const url = `/shortlistedCandidates/screen-with-ai/${encryptedId}`;
            const resData = await dispatch(postData(url, {}));

            if (resData?.code === 200) {
                const decryptedData = decryptData(resData.data);
                const screeningResult = decryptedData?.screeningResult;

                if (screeningResult) {
                    setScreeningData({
                        overallScore: screeningResult.overallScore || screeningResult.overallMatchPercentage,
                        jdMatchScore: screeningResult.jdMatchScore,
                        experienceScore: screeningResult.experienceScore,
                        skillsMatchScore: screeningResult.skillsMatchScore,
                        strengths: screeningResult.strengths || [],
                        weaknesses: screeningResult.weaknesses || [],
                        recommendation: screeningResult.recommendation,
                        detailedAnalysis: screeningResult.detailedAnalysis,
                        screenedAt: decryptedData?.screenedAt || new Date()
                    });

                    setShowScreeningModal(true);

                    if (!decryptedData?.cached) {
                        notification.success({
                            message: 'AI Screening Completed',
                            description: 'Screening results are now available.',
                            duration: 3,
                        });
                    }
                } else {
                    throw new Error('Invalid response from AI screening');
                }
            } else {
                throw new Error(resData?.message || 'Failed to screen candidate');
            }
        } catch (error: any) {
            console.error('Error screening candidate with AI:', error);
            notification.error({
                message: 'Screening Failed',
                description: error?.message || 'Failed to screen candidate with AI. Please try again.',
                duration: 5,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleViewScore = () => {
        if (candidate?.aiScreening) {
            setScreeningData(candidate.aiScreening);
            setShowScreeningModal(true);
        }
    };


    const scrollTo = (id: string) => {
        const section = sectionRefs.current[id];
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlighted(id); // Highlight

            setTimeout(() => {
                setHighlighted(current => (current === id ? null : current));
            }, 1000 * 5);
        }
    };

    return (
        <div className="flex overflow-hidden">
            <div className="w-64 bg-white shadow-sm border-r  h-[calc(100vh-60px)] p-4 flex-shrink-0">
                <Text strong className="block mb-4">Related List</Text>
                <ul className="text-sm">
                    {sections.map(section => (
                        <li
                            key={section.id}
                            onClick={() => scrollTo(section.id)}
                            className="cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-xm group"
                        >
                            <div className='flex items-center justify-between h-5'>
                                {section.title}
                                <Button type='text' className='hidden group-hover:block' icon={<PlusCircleOutlined className='pt-1' />} size="small" />
                            </div>
                        </li>
                    ))}

                    <li className="text-blue-600 cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-sm">Add Related List</li>
                </ul>
                <Text strong className="block mt-6 mb-2 ">Links</Text>
                <p className="text-blue-600 cursor-pointer text-sm hover:bg-gray-100 py-2 px-2 rounded-sm">Add Link</p>
            </div>

            <div className="flex-1 overflow-y-auto h-full max-h-[calc(100vh-60px)] p-2">
                <div className="flex-1 p-2">
                    <div className='flex items-center justify-between mb-4 '>
                        <Segmented
                            options={[
                                { label: 'Overview', value: 'overview' },
                                { label: 'Timeline', value: 'timeline' },
                            ]}
                            value={activeTab}
                            onChange={value => setActiveTab(value as 'overview' | 'timeline')}
                        />
                        <div>
                            <Text className='text-sm text-gray-500'>Last Update : {timeAgo(candidate?.updatedAt)}</Text>
                        </div>
                    </div>

                    {activeTab === 'overview' && (<div className='space-y-4'>
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={candidateOwnerConfig}
                                        id={candidate?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={candidateConfig}
                                        id={candidate?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>

                        {sections.map(section => (
                            <div
                                key={section.id}
                                ref={el => (sectionRefs.current[section.id] = el)}
                                className="bg-white rounded-md mb-6"
                            >
                                {section.id === 'ai-screening' && (
                                    <>
                                        <div className="flex justify-between items-center py-2 px-4 border-b">
                                            <Title level={5} className={highlighted === section.id ? 'bg-yellow-200 px-1' : ''}>
                                                AI Screening
                                            </Title>
                                        </div>
                                        <div className="p-4">
                                            {candidate?.aiScreening?.overallScore !== null && candidate?.aiScreening?.overallScore !== undefined ? (
                                                <div className="flex items-center gap-4">
                                                    <Tooltip
                                                        title={
                                                            <div>
                                                                <div>JD Match: {candidate?.aiScreening?.jdMatchScore || 'N/A'}/100</div>
                                                                <div>Experience: {candidate?.aiScreening?.experienceScore || 'N/A'}/100</div>
                                                                <div>Skills: {candidate?.aiScreening?.skillsMatchScore || 'N/A'}/100</div>
                                                                {candidate?.aiScreening?.screenedAt && (
                                                                    <div style={{ marginTop: '4px', fontSize: '11px', color: '#ccc' }}>
                                                                        Screened: {new Date(candidate.aiScreening.screenedAt).toLocaleDateString()}
                                                                    </div>
                                                                )}
                                                                <div style={{ marginTop: '8px', fontSize: '11px', color: '#999' }}>
                                                                    Click to view details
                                                                </div>
                                                            </div>
                                                        }
                                                    >
                                                        <Tag
                                                            color={getScoreColor(candidate.aiScreening.overallScore)}
                                                            style={{ fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', padding: '8px 16px' }}
                                                            onClick={handleViewScore}
                                                        >
                                                            {candidate.aiScreening.overallScore}/100 📋
                                                        </Tag>
                                                    </Tooltip>
                                                    <Text type="secondary">
                                                        {candidate?.aiScreening?.recommendation || 'Not Recommended'}
                                                    </Text>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-4">
                                                    <Button
                                                        type="primary"
                                                        icon={<RobotOutlined />}
                                                        loading={loading}
                                                        onClick={handleScreenWithAI}
                                                    >
                                                        Screen with AI
                                                    </Button>
                                                    <Text type="secondary">No screening results available</Text>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                                {!['Attachments', 'Feedback', 'Invited Meetings', 'AI Screening'].includes(section.title) && <div className="flex justify-between items-center py-2 px-4 border-b">
                                    < Title level={5} className={highlighted === section.id ? 'bg-yellow-200 px-1' : ''}>{section.title}</Title>
                                    {section.title !== 'Notes' && <Button type="text" disabled>Add {section.title}</Button>}
                                </div>}
                                {!['Notes', 'Attachments', 'Feedback', 'Invited Meetings', 'AI Screening'].includes(section.title) && <div className="p-4 text-gray-300 text-center">No {section.title.toLowerCase()} available</div>}
                                {section.title === 'Notes' && <NotesComponent refType={'ShortlistedCandidatesForInterview'} refId={candidate?._id} />}
                                {section.title === 'Attachments' && <div className='p-4'><Attachments refType={'ShortlistedCandidatesForInterview'} refId={candidate?._id} /> </div>}
                                {section.id == 'feedback' && <CandidateFeedback refId={candidate?._id} highlighted={highlighted} level={candidate?.level} />}
                                {section.id == 'invited-meetings' && <Meetings refId={candidate?._id} highlighted={highlighted} level={candidate?.level} />}
                            </div>
                        ))}
                    </div>)}
                    {activeTab === 'timeline' && <Timeline type="ShortlistedCandidatesForInterview" id={candidate?._id} />}
                </div>
            </div>

            {/* AI Screening Results Modal */}
            <Modal
                title={
                    <Space>
                        <RobotOutlined />
                        <span>AI Screening Results</span>
                        {screeningData?.screenedAt && (
                            <Tag color="blue" style={{ fontSize: '11px' }}>
                                Screened: {new Date(screeningData.screenedAt).toLocaleString()}
                            </Tag>
                        )}
                    </Space>
                }
                open={showScreeningModal}
                onCancel={() => setShowScreeningModal(false)}
                footer={[
                    <Button key="close" onClick={() => setShowScreeningModal(false)}>
                        Close
                    </Button>
                ]}
                width={700}
            >
                {screeningData && (
                    <div style={{ padding: '10px 0' }}>
                        {/* Overall Score */}
                        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                            <Typography.Title level={2} style={{ margin: 0, color: getScoreColor(screeningData.overallScore || screeningData.overallMatchPercentage || 0) }}>
                                {screeningData.overallScore || screeningData.overallMatchPercentage || 0}/100
                            </Typography.Title>
                            <Tag
                                color={getRecommendationColor(screeningData.recommendation || '')}
                                style={{ fontSize: '14px', padding: '4px 12px', marginTop: '8px' }}
                            >
                                {screeningData.recommendation || 'Not Recommended'}
                            </Tag>
                        </div>

                        <Divider />

                        {/* Score Breakdown */}
                        <div style={{ marginBottom: '24px' }}>
                            <Typography.Title level={5}>Score Breakdown</Typography.Title>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span>JD Match Score</span>
                                        <span><strong>{screeningData.jdMatchScore || 0}/100</strong></span>
                                    </div>
                                    <Progress
                                        percent={screeningData.jdMatchScore || 0}
                                        strokeColor={getScoreColor(screeningData.jdMatchScore || 0)}
                                        showInfo={false}
                                    />
                                </div>
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span>Experience Match</span>
                                        <span><strong>{screeningData.experienceScore || 0}/100</strong></span>
                                    </div>
                                    <Progress
                                        percent={screeningData.experienceScore || 0}
                                        strokeColor={getScoreColor(screeningData.experienceScore || 0)}
                                        showInfo={false}
                                    />
                                </div>
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span>Skills Match</span>
                                        <span><strong>{screeningData.skillsMatchScore || 0}/100</strong></span>
                                    </div>
                                    <Progress
                                        percent={screeningData.skillsMatchScore || 0}
                                        strokeColor={getScoreColor(screeningData.skillsMatchScore || 0)}
                                        showInfo={false}
                                    />
                                </div>
                            </Space>
                        </div>

                        <Divider />

                        {/* Strengths */}
                        {screeningData.strengths && screeningData.strengths.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                                <Typography.Title level={5} style={{ color: '#52c41a' }}>Strengths</Typography.Title>
                                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                    {screeningData.strengths.map((strength: string, index: number) => (
                                        <li key={index} style={{ marginBottom: '4px' }}>
                                            <Typography.Text>{strength}</Typography.Text>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Weaknesses */}
                        {screeningData.weaknesses && screeningData.weaknesses.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                                <Typography.Title level={5} style={{ color: '#ff4d4f' }}>Weaknesses</Typography.Title>
                                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                    {screeningData.weaknesses.map((weakness: string, index: number) => (
                                        <li key={index} style={{ marginBottom: '4px' }}>
                                            <Typography.Text type="danger">{weakness}</Typography.Text>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Detailed Analysis */}
                        {screeningData.detailedAnalysis && (
                            <div>
                                <Typography.Title level={5}>Detailed Analysis</Typography.Title>
                                <Typography.Paragraph style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                                    {screeningData.detailedAnalysis}
                                </Typography.Paragraph>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div >
    );
};

export default CandidateDetails 